/** The page header card: the place's icon, its title (the page's `<h1>`), a small label and the actions on
 * the right - ADR-0212 decision 2, and the one header every page now shares (U6 and U9, 2026-11-03).
 *
 * `TablePage` draws it above the tiles of a data place; the older pages (a site, Past filings, Help, Bulk
 * edit, Settings, Support, Admin for a role that is not an Admin) draw it on its own as the first thing in
 * their body, so a person who moves from Facilities to a site does not see the title jump from a card to the
 * top bar. It is **72px tall on every page** and the title is one line, cut with an ellipsis: a header that
 * wraps to two lines is a header that moves the page under it.
 *
 * It renders nothing but its slots (no `children`), like `TablePage`: a sentence does not belong in a header.
 */

import type { Icon } from "@phosphor-icons/react";
import type { ReactNode } from "react";

import { PageTitle } from "./PageTitle";
import styles from "./PageHeaderCard.module.css";

export interface PageHeaderCardProps {
  icon: Icon;
  /** The place's name, and the page's `<h1>`. */
  title: string;
  /** A small label beside the title: the tab, or what kind of thing the page is about. */
  label?: string;
  actions?: ReactNode;
}

export function PageHeaderCard({ icon: Glyph, title, label, actions }: PageHeaderCardProps) {
  return (
    <header className={`card ${styles.head}`} data-page-header>
      <span className={styles.badge} aria-hidden="true">
        <Glyph size={22} weight="duotone" />
      </span>
      <div className={styles.titles}>
        <PageTitle>{title}</PageTitle>
        {label ? (
          <span className={styles.label} title={label.length > 36 ? label : undefined}>
            {label}
          </span>
        ) : null}
      </div>
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </header>
  );
}
